import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActivityLogs } from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, SearchInput,
} from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatDateTime } from '../../utils/format';
import { ScrollText } from 'lucide-react';

const LIMIT = 20;

export default function ActivityLogsPage() {
  const [page, setPage] = useState(1);
  const [actorType, setActorType] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-activity-logs', page, actorType, action, from, to],
    queryFn: () =>
      getActivityLogs({
        page,
        limit: LIMIT,
        actorType: actorType || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to || undefined,
      }).then((r) => r.data.data),
    placeholderData: (prev) => prev,
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'actorType', label: 'Actor type' },
    { key: 'action', label: 'Action' },
    { key: 'createdAt', label: 'When' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="Activity Logs" subtitle="Admin and system audit trail (PDF §36)">
        <ExportButton data={rows} filename="activity-logs" columns={EXPORT_COLS} />
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'Total', value: pagination?.total ?? '—', icon: <ScrollText size={14} /> },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5 flex flex-wrap gap-3 items-end">
        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Actor type</label>
          <select className="input" value={actorType} onChange={(e) => { setActorType(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="astrologer">Astrologer</option>
            <option value="system">System</option>
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
          <SearchInput value={action} onChange={(v) => { setAction(v); setPage(1); }} placeholder="Filter action…" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={10} cols={5} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No activity logs" icon={<ScrollText size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">When</th>
                <th className="table-th">Actor</th>
                <th className="table-th">Action</th>
                <th className="table-th">Target</th>
                <th className="table-th">Meta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((log: any) => (
                <tr key={log._id} className="table-row">
                  <td className="table-td text-xs whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                  <td className="table-td text-xs">
                    <span className="capitalize">{log.actorType || '—'}</span>
                    {log.actorId ? <span className="block text-gray-400 font-mono">{String(log.actorId).slice(-8)}</span> : null}
                  </td>
                  <td className="table-td text-sm font-medium">{log.action}</td>
                  <td className="table-td text-xs">{log.targetType || log.entityType || '—'} {log.targetId ? String(log.targetId).slice(-6) : ''}</td>
                  <td className="table-td text-[10px] text-gray-500 max-w-[200px] truncate">
                    {log.metadata ? JSON.stringify(log.metadata) : log.details || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
